import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Button } from "@learn-me/ui";
import { X } from "lucide-react";
import type { NewBadge } from "../api/gamificationApi";

interface BadgeCelebrationProps {
  badges: NewBadge[];
  onClose: () => void;
}

export function BadgeCelebration({ badges, onClose }: BadgeCelebrationProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (badges.length > 0) {
      // Lanzar confetti
      const duration = 2000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#f59e0b", "#f97316", "#fbbf24"],
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#f59e0b", "#f97316", "#fbbf24"],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();
    }
  }, [badges.length, currentIndex]);

  if (badges.length === 0) return null;

  const currentBadge = badges[currentIndex];
  const hasMore = currentIndex < badges.length - 1;

  const handleNext = () => {
    if (hasMore) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          className="relative bg-white rounded-2xl p-8 max-w-sm mx-4 text-center shadow-2xl"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          <motion.div
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-lg font-semibold text-amber-600 mb-4"
          >
            Nuevo Badge Desbloqueado
          </motion.div>

          <motion.div
            key={currentBadge.badgeId}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 10 }}
            className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-6xl shadow-lg shadow-amber-200"
          >
            {currentBadge.icon}
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold text-gray-900 mb-2"
          >
            {currentBadge.name}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-gray-600 mb-6"
          >
            Felicitaciones por tu logro
          </motion.p>

          <div className="flex justify-center gap-2">
            {badges.length > 1 && (
              <div className="flex gap-1 mb-4">
                {badges.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full ${
                      idx === currentIndex ? "bg-amber-500" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleNext} className="w-full">
            {hasMore ? "Siguiente" : "Continuar"}
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
